import { Controller } from "@hotwired/stimulus"
import SignaturePad from "signature_pad"
import { showToast } from "../lib/toast"

// The signature box in the finance Sign & Approve pop-ups (CL1-1, Phelipe
// 2026-10-10). Three tabs: Type (the account's name in script, nothing to
// edit), Draw (a pad; Use this signature saves the drawing) and Upload (a
// PNG or JPG, saved the moment it's chosen). What's saved is the person's
// signature from then on, in every pop-up and on the signed PDFs, and the
// box reopens on the tab they last used. Picking Type takes the picture
// away. A page can hold several pop-ups, each with its own box: a change in
// one is told to the others (signature:changed), so they all agree.
const ON = ["bg-brand/10", "text-brand"]
const OFF = ["text-gray-600", "hover:bg-gray-100"]
const MAX_BYTES = 1024 * 1024
const TYPES = ["image/png", "image/jpeg"]

export default class extends Controller {
  static targets = ["tab", "pane", "status", "canvas", "drawImage", "drawPad", "drawAgain", "clear", "saveDraw", "uploadImage", "file", "error"]
  static values = { url: String, kind: String, image: String, text: Object }

  connect() {
    this.shown = this.kindValue || "typed"
    this._onChanged = (e) => {
      if (!e.detail || e.detail.source === this) return
      this.kindValue = e.detail.kind
      this.imageValue = e.detail.url || ""
      this.redrawing = false
      this.shown = this.kindValue
      this._render()
    }
    window.addEventListener("signature:changed", this._onChanged)
    this._render()
  }

  disconnect() {
    window.removeEventListener("signature:changed", this._onChanged)
    if (this.pad) this.pad.off()
  }

  t(key, vars = {}) {
    let s = key.split(".").reduce((o, k) => o?.[k], this.textValue) ?? ""
    for (const [k, v] of Object.entries(vars)) s = s.replace(`%{${k}}`, v)
    return s
  }

  // A tab. Type is the saved choice the moment it's picked; Draw and
  // Upload save when there is something to save.
  pick(e) {
    const kind = e.currentTarget.dataset.kind
    this._error(null)
    if (kind === "typed") return this.useTyped()
    this.shown = kind
    this._render()
  }

  async useTyped() {
    this.shown = "typed"
    if (this.kindValue === "typed") return this._render()
    const data = await this._request("DELETE")
    if (!data) return
    this._saved("typed", "")
    showToast(this.t("typed_now"))
  }

  // ── Draw ───────────────────────────────────────────────────────────

  drawAgain() {
    this.redrawing = true
    if (this.pad) this.pad.clear()
    this._render()
  }

  clear() {
    if (this.pad) this.pad.clear()
    this._drawChanged()
  }

  async saveDraw() {
    if (!this.pad || this.pad.isEmpty()) return
    const data = await this._request("POST", { digital_signature: this._drawing(), kind: "draw" })
    if (!data) return
    this.redrawing = false
    this._saved("draw", data.url)
    showToast(this.t("saved"))
  }

  _drawChanged() {
    if (this.hasSaveDrawTarget) this.saveDrawTarget.disabled = !this.pad || this.pad.isEmpty()
  }

  // The drawing cut down to what was drawn, with a little room around it,
  // so it shows and prints at a good size instead of as a small mark in a
  // wide empty strip. Falls back to the whole pad.
  _drawing() {
    const whole = this.pad.toDataURL("image/png")
    try {
      const canvas = this.canvasTarget
      const { width, height } = canvas
      const px = canvas.getContext("2d").getImageData(0, 0, width, height).data
      let top = height, left = width, bottom = -1, right = -1
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          if (px[(y * width + x) * 4 + 3] === 0) continue
          if (y < top) top = y
          if (y > bottom) bottom = y
          if (x < left) left = x
          if (x > right) right = x
        }
      }
      if (bottom < 0) return whole
      const room = Math.round(8 * Math.max(window.devicePixelRatio || 1, 1))
      left = Math.max(0, left - room)
      top = Math.max(0, top - room)
      right = Math.min(width - 1, right + room)
      bottom = Math.min(height - 1, bottom + room)
      const out = document.createElement("canvas")
      out.width = right - left + 1
      out.height = bottom - top + 1
      out.getContext("2d").drawImage(canvas, left, top, out.width, out.height, 0, 0, out.width, out.height)
      return out.toDataURL("image/png")
    } catch (_) {
      return whole
    }
  }

  // The pad is made the first time the Draw tab shows with its pane
  // visible, so the canvas has a size to take.
  _ensurePad() {
    if (this.pad || !this.hasCanvasTarget) return
    const canvas = this.canvasTarget
    const ratio = Math.max(window.devicePixelRatio || 1, 1)
    const rect = canvas.getBoundingClientRect()
    canvas.width = Math.round((rect.width || 400) * ratio)
    canvas.height = Math.round((rect.height || 112) * ratio)
    const ctx = canvas.getContext && canvas.getContext("2d")
    if (!ctx) return
    ctx.scale(ratio, ratio)
    try {
      this.pad = new SignaturePad(canvas, { backgroundColor: "rgba(255,255,255,0)", penColor: "#1f2937" })
      this.pad.addEventListener("endStroke", () => this._drawChanged())
    } catch (_) {
      this.pad = null
    }
  }

  // ── Upload ─────────────────────────────────────────────────────────

  fileChosen(e) {
    const file = e.currentTarget.files[0]
    e.currentTarget.value = ""
    if (!file) return
    if (!TYPES.includes(file.type)) return this._error(this.t("errors.wrong_type"))
    if (file.size > MAX_BYTES) return this._error(this.t("errors.too_big"))
    const reader = new FileReader()
    reader.onload = async () => {
      const data = await this._request("POST", { digital_signature: reader.result, kind: "upload" })
      if (!data) return
      this._saved("upload", data.url)
      showToast(this.t("saved"))
    }
    reader.readAsDataURL(file)
  }

  // ── Drawing the box ────────────────────────────────────────────────

  _saved(kind, url) {
    this.kindValue = kind
    this.imageValue = url || ""
    this.shown = kind
    if (kind !== "draw" && this.pad) this.pad.clear()
    this._error(null)
    this._render()
    window.dispatchEvent(new CustomEvent("signature:changed", { detail: { kind, url: this.imageValue, source: this } }))
  }

  _render() {
    const shown = this.shown || "typed"
    this.tabTargets.forEach((tab) => {
      const on = tab.dataset.kind === shown
      ON.forEach((c) => tab.classList.toggle(c, on))
      OFF.forEach((c) => tab.classList.toggle(c, !on))
      tab.setAttribute("aria-selected", on ? "true" : "false")
    })
    this.paneTargets.forEach((pane) => { pane.hidden = pane.dataset.kind !== shown })

    const url = this.imageValue
    const hasDraw = this.kindValue === "draw" && !!url && !this.redrawing
    if (this.hasDrawImageTarget) { this.drawImageTarget.src = hasDraw ? url : ""; this.drawImageTarget.hidden = !hasDraw }
    if (this.hasDrawPadTarget) this.drawPadTarget.hidden = hasDraw
    if (this.hasDrawAgainTarget) this.drawAgainTarget.hidden = !hasDraw
    if (this.hasClearTarget) this.clearTarget.hidden = hasDraw
    if (this.hasSaveDrawTarget) this.saveDrawTarget.hidden = hasDraw
    const hasUpload = this.kindValue === "upload" && !!url
    if (this.hasUploadImageTarget) { this.uploadImageTarget.src = hasUpload ? url : ""; this.uploadImageTarget.hidden = !hasUpload }
    if (this.hasStatusTarget) this.statusTarget.textContent = this.t(`status_${this.kindValue || "typed"}`)

    if (shown === "draw" && !hasDraw) {
      this._ensurePad()
      this._drawChanged()
    }
  }

  _error(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message || ""
    this.errorTarget.hidden = !message
  }

  async _request(method, body) {
    const headers = { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "", "Accept": "application/json" }
    let payload
    if (body) {
      headers["Content-Type"] = "application/json"
      payload = JSON.stringify(body)
    }
    try {
      const res = await fetch(this.urlValue, { method, headers, body: payload, credentials: "same-origin" })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        this._error(data.error || this.t("errors.generic"))
        return null
      }
      return data
    } catch (_) {
      this._error(this.t("errors.generic"))
      return null
    }
  }
}
